import { useCallback, useEffect, useState } from "react";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import useTabs, { SetActiveTabFunctionType } from "../../hooks/use-tabs/useTabs";
import { useAvailableObjectDetailsTabs } from "../../queries/available-views/UseAvailableObjectDetailsTabs";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { routesByViewID } from "../../routes/routes";
import { isMobile, isTablet } from 'react-device-detect';
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { ActionMenuItem, BaseActionEnum, BaseActions } from "../../components/action-menu/ActionMenuItem";
import { useObjectProperties } from "../../queries/object-properties/UseObjectProperties";
import { IObjectPropertiesDeviceModel, IObjectPropertiesTankBatteryModel, IObjectPropertiesTankModel, IObjectPropertiesWellModel } from "../../services/properties-data-service/Models";
import { UseQueryResult } from "react-query";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import { ObjectFeatures2Enum } from "../../common/enums/ObjectFeatures2Enum";
import { CustomerTypeEnum } from "../../common/enums/CustomerTypeEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { ConfigurationActionItem } from "../../components/action-menu/ConfigurationActions";
import { objectActionItems } from "./ObjectActions";
import Enumerable from "linq";
import { AccessTypeEnum } from "../../common/enums/AccessTypeEnum";
import { AccessFunctionalityTypeEnum } from "../../common/enums/AccessFunctionalityTypeEnum";
import { PlatformObjectTypeEnum } from "../../common/enums/PlatformObjectTypeEnum";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../services/features-data-service/Models";

export enum ObjectDetailsViewTabEnum {
    None = -1,
    Values = 0,
    Properties = 1,
    Defaults = 2,
    AlarmSetpoints = 3,
    Attachments = 4,
    History = 5,
    //Membership = 6,
    Relationships = 7,
    RunTickets = 8,
    ExternalContent = 9
};


type UseObjectDetailsViewStateType = {
    deviceType?: DeviceTypesEnum,
    activeTab: ObjectDetailsViewTabEnum | undefined;
    setActiveTab: SetActiveTabFunctionType<ObjectDetailsViewTabEnum>;

    actions: ActionMenuItem[];
    setActions: (items: ActionMenuItem[]) => void;
    resetActions: () => void;

    viewOptionState: ViewOptionStateEnum;
    setViewOptionState: (option: ViewOptionStateEnum) => void;

    hasSettingsChanges: boolean;
    hasAlarmHistory: boolean;
    setHasSettingsChanges: (hasSettingsChanges: boolean) => void;
    setHasAlarmHistory: (hasAlarmHistory: boolean) => void;

    latitude?: string;
    longitude?: string;

    configurationActions: ConfigurationActionItem[];
    setConfigurationsActions: (items: ConfigurationActionItem[]) => void;
};

export function useObjectDetailsViewState(
    objectType: eLynxObjectTypeEnum,
    // configurationEnabled: boolean
): UseObjectDetailsViewStateType {

    const objectDetailsTabs = getTabList(objectType);
    //const [, setActions_old] = useActions(onExecuteAction);

    const [viewOptionState, setViewOptionState] = useState(ViewOptionStateEnum.Default);
    const [actions, _setActions] = useState<ActionMenuItem[]>([]);
    const [configurationActions, setConfigurationsActions] = useState<ConfigurationActionItem[]>([]);


    const [hasSettingsChanges, setHasSettingsChanges] = useState(false);
    const [hasAlarmHistory, setHasAlarmHistory] = useState(false);

    const [activeTab, setActiveTab] = useTabs<ObjectDetailsViewTabEnum>(
        eLynxObjectTypeEnum[objectType],
        objectDetailsTabs,
        ObjectDetailsViewTabEnum.Values
    );

    const { data: headerAndActionsV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.HeaderAndActionsV2,
    });

    const headerAndActionsV2 = (headerAndActionsV2Feature ?? false);

    const setActions = useCallback((items: ActionMenuItem[]) => {

        const newActions = [...items].sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
        _setActions(newActions);
    }, [_setActions]);


    const isPhone = isMobile && !isTablet;

    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();

    const { data: objectViewState } = useObjectViewState({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: offlineState.offline
    });

    const { data: availableTabs } = useAvailableObjectDetailsTabs({ enabled: true });

    const { data: properties } = useQueryNotifications(useObjectProperties)({
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject
    }) as UseQueryResult<IObjectPropertiesDeviceModel |
                IObjectPropertiesTankModel |
                IObjectPropertiesTankBatteryModel |
                IObjectPropertiesWellModel | undefined>;

    const hasLatLong = properties?.latitude && properties?.longitude ? true : false;

    const hasExternalContent = ((objectViewState?.features2 ?? ObjectFeatures2Enum.None) & ObjectFeatures2Enum.AdditionalContent) && 
                                    objectViewState?.externalContent?.url;

    useEffect(() => {
        if(availableTabs) {
            let alternateTab: ObjectDetailsViewTabEnum = ObjectDetailsViewTabEnum.None;

            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab <= ObjectDetailsViewTabEnum.Values && availableTabs?.hasValues ? ObjectDetailsViewTabEnum.Values : alternateTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab <= ObjectDetailsViewTabEnum.Properties && availableTabs?.hasProperties ? ObjectDetailsViewTabEnum.Properties : alternateTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab <= ObjectDetailsViewTabEnum.Defaults && availableTabs?.hasDefaults ? ObjectDetailsViewTabEnum.Defaults : alternateTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab <= ObjectDetailsViewTabEnum.AlarmSetpoints && availableTabs?.hasAlarmSetpoints ? ObjectDetailsViewTabEnum.AlarmSetpoints : alternateTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab <= ObjectDetailsViewTabEnum.Attachments && availableTabs?.hasAttachments ? ObjectDetailsViewTabEnum.Attachments : alternateTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab <= ObjectDetailsViewTabEnum.History && availableTabs?.hasHistory ? ObjectDetailsViewTabEnum.History : alternateTab;
            //activeTab = activeTab === ObjectDetailsViewTabEnum.None && preferredTab <= ObjectDetailsViewTabEnum.Membership && state?.hasMembership ? ObjectDetailsViewTabEnum.Membership : activeTab;
    
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab <= ObjectDetailsViewTabEnum.Relationships && availableTabs?.hasRelationships ? ObjectDetailsViewTabEnum.Relationships : alternateTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab <= ObjectDetailsViewTabEnum.RunTickets && availableTabs?.hasRunTickets ? ObjectDetailsViewTabEnum.RunTickets : alternateTab;
    
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab > ObjectDetailsViewTabEnum.Relationships && hasExternalContent && !offlineState.offline ? ObjectDetailsViewTabEnum.ExternalContent : alternateTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab > ObjectDetailsViewTabEnum.RunTickets && availableTabs?.hasRunTickets ? ObjectDetailsViewTabEnum.RunTickets : alternateTab;
            // activeTab = activeTab === ObjectDetailsViewTabEnum.None && preferredTab > ObjectDetailsViewTabEnum.Relationships && state?.hasRelationships ? ObjectDetailsViewTabEnum.Relationships : activeTab;
            // activeTab = activeTab === ObjectDetailsViewTabEnum.None && preferredTab > ObjectDetailsViewTabEnum.Membership && state?.hasMembership ? ObjectDetailsViewTabEnum.Membership : activeTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab > ObjectDetailsViewTabEnum.History && availableTabs?.hasHistory ? ObjectDetailsViewTabEnum.History : alternateTab;
            // activeTab = activeTab === ObjectDetailsViewTabEnum.None && preferredTab > ObjectDetailsViewTabEnum.Attachments && state?.hasAttachments ? ObjectDetailsViewTabEnum.Attachments : activeTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab > ObjectDetailsViewTabEnum.AlarmSetpoints && availableTabs?.hasAlarmSetpoints ? ObjectDetailsViewTabEnum.AlarmSetpoints : alternateTab;
            // activeTab = activeTab === ObjectDetailsViewTabEnum.None && preferredTab > ObjectDetailsViewTabEnum.Defaults && state?.hasDefaults ? ObjectDetailsViewTabEnum.Defaults : activeTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab > ObjectDetailsViewTabEnum.Properties && availableTabs?.hasProperties ? ObjectDetailsViewTabEnum.Properties : alternateTab;
            alternateTab = alternateTab === ObjectDetailsViewTabEnum.None && activeTab > ObjectDetailsViewTabEnum.Values && availableTabs?.hasValues ? ObjectDetailsViewTabEnum.Values : alternateTab;
    
            if(alternateTab !== activeTab) {
                setActiveTab(alternateTab, true);
            }
        }
    }, [
        activeTab,
        availableTabs,
        hasExternalContent,
        offlineState.offline,
        setActiveTab
    ]);
    const resetActions = useCallback(() => {

        if (objectViewState) {
            //const viewState = contextViewState?.viewState as IObjectViewState;
            const features = objectViewState.features;

            // need to move this into a utility somewhere
            const updateAndAddMenuItem = (results: ActionMenuItem[], action: BaseActions, disabled: boolean | undefined) => {
                const menuItem = objectActionItems.find(o => o.action === action);
                
                if (menuItem) {
                    results.push({...menuItem,
                        disabled: disabled,
                        order: menuItem.order
                    });
                }
            };

            // set available actions
            const currentActions: ActionMenuItem[] =  [];

            if (availableTabs?.hasValues) {
                // if ((features & ObjectFeaturesEnum.ManualEntry) > 0) {
                //     updateAndAddMenuItem(currentActions, ContentLayoutActionEnum.ManualEntry, undefined);
                // }

                if ((features & ObjectFeaturesEnum.LiveData) > 0) {
                    updateAndAddMenuItem(currentActions, BaseActionEnum.DemandScan, undefined);
                }

                if ((features & ObjectFeaturesEnum.Writedown) > 0) {
                    updateAndAddMenuItem(currentActions, BaseActionEnum.WriteDown, undefined);
                }
            }


            if (!isPhone) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.Refresh, undefined);
            }


            if ((objectType === eLynxObjectTypeEnum.Device || objectType === eLynxObjectTypeEnum.Tank) &&
                    (activeTab === ObjectDetailsViewTabEnum.Values || 
                    (activeTab === ObjectDetailsViewTabEnum.History && 
                    (hasAlarmHistory || hasSettingsChanges)))) {

                updateAndAddMenuItem(currentActions, BaseActionEnum.Export, undefined);
            }

            if (activeTab === ObjectDetailsViewTabEnum.RunTickets) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddRunTicket, undefined);
            }

            // if (availableTabs?.hasProperties) {
            // }

            // if (state?.hasDefaults) {

            // }

            // if (availableTabs?.hasAlarmSetpoints) {

            // }

            // if (state?.hasAttachments) {

            // }

            if (availableTabs?.hasHistory && properties?.canModifyMemos) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddMemo, undefined);
            }

            if (/*!isPhone &&*/ !offlineState.offline && availableTabs?.hasAttachments && properties?.canModifyAttachments) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddAttachment, undefined);
            }

            if (!isPhone && !offlineState.offline && objectType !== eLynxObjectTypeEnum.TankBattery && userState.customer?.customerType === CustomerTypeEnum.Energy) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddDiagnositicEvent, undefined);
            }

            if (!offlineState.offline && objectType === eLynxObjectTypeEnum.Device && userState.customer?.customerType === CustomerTypeEnum.Water) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddEvent, undefined);
            }

            // if (state?.hasMembership) {

            // }

            // if (state?.hasRelationships) {

            // }

            if (availableTabs?.hasRunTickets) {
                if ((objectViewState.features & ObjectFeaturesEnum.ManualEntry) > 0 || objectType !== eLynxObjectTypeEnum.Device) {
                    if (!currentActions.some(o => o.action === BaseActionEnum.AddRunTicket)) {  
                        updateAndAddMenuItem(currentActions, BaseActionEnum.AddRunTicket, undefined);
                    }
                }
            }

            if (!offlineState.offline && properties?.canModify) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddAttribute, undefined);
            }


            if (!offlineState.offline && ((objectViewState?.features2 ?? ObjectFeatures2Enum.None) & ObjectFeatures2Enum.ModifyPollingSchedule) > 0) {

                if (objectType === eLynxObjectTypeEnum.Device || objectType === eLynxObjectTypeEnum.Tank) {
                    const deviceProperties = properties as IObjectPropertiesDeviceModel;
                    if (deviceProperties && deviceProperties.tagPollingSchedules.length > 0) {
                        updateAndAddMenuItem(currentActions, BaseActionEnum.ModifyPollingSchedule, undefined);
                    }
                }
                
            }

            //setActions_old(currentActions);

            if (objectType === eLynxObjectTypeEnum.TankBattery || objectType === eLynxObjectTypeEnum.Tank) {
                const memoAction = currentActions.find(o => o.action === BaseActionEnum.AddMemo);
                const runTicketAction = currentActions.find(o => o.action === BaseActionEnum.AddRunTicket);

                if (memoAction && runTicketAction) {
                    const lowest = Math.min(memoAction.order ?? 0, runTicketAction.order ?? 0);
                    runTicketAction.order = lowest;
                    memoAction.order = lowest + 1;
                }
            }

            updateAndAddMenuItem(currentActions, BaseActionEnum.Favorite, undefined);


            if (hasLatLong) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.MapIt, undefined);
            }


            if (!offlineState.offline && objectViewState.hybridServicesR1Enabled && objectViewState.platformObjectType === PlatformObjectTypeEnum.PollingServer) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.ManageComm, undefined);
            }

            // TODO: Limited to devices for now...that's all the user story called for.
            if(!offlineState.offline && objectType === eLynxObjectTypeEnum.Device) {


                const hasAlarmPermissions = objectViewState?.accessPermissions?.some(o => o.functionalityType === AccessFunctionalityTypeEnum.Alarming && (o.access & AccessTypeEnum.Write_Change) > 0) ?? false;

                if (hasAlarmPermissions)
                {
                    updateAndAddMenuItem(currentActions, BaseActionEnum.DisableAllAlarms, undefined);
                    updateAndAddMenuItem(currentActions, BaseActionEnum.EnableAllAlarms, undefined);

                    const attachmentAction = currentActions.find(o => o.action === BaseActionEnum.AddAttachment);

                    const enableAlarmsAction = currentActions.find(o => o.action === BaseActionEnum.EnableAllAlarms);

                    const disableAlarmsAction = currentActions.find(o => o.action === BaseActionEnum.DisableAllAlarms);
                    const applyAttributeAction = currentActions.find(o => o.action === BaseActionEnum.AddAttribute);

                    const mapAction = currentActions.find(o => o.action === BaseActionEnum.MapIt);

                    if (attachmentAction && enableAlarmsAction && disableAlarmsAction && applyAttributeAction && mapAction) {
                        let neworder = attachmentAction.order! + 1;
                        mapAction.order = neworder++;
                        applyAttributeAction.order = neworder++;
                        disableAlarmsAction.order = neworder++;
                        enableAlarmsAction.order = neworder;
                    }
                }
            }

            let maxOrder = Enumerable.from(currentActions).max(o => o.order ?? 0) + 1;
            let actionToUpdate = currentActions.find(o => o.action === BaseActionEnum.Export);
            if (actionToUpdate) {
                actionToUpdate.order = maxOrder++;
            }

            actionToUpdate = currentActions.find(o => o.action === BaseActionEnum.Refresh);
            if (actionToUpdate) {
                actionToUpdate.order = maxOrder++;
            }

            actionToUpdate = currentActions.find(o => o.action === BaseActionEnum.Favorite);
            if (actionToUpdate) {
                actionToUpdate.order = maxOrder;
            }

            setActions(currentActions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        }
    }, [
        objectViewState,
        setActions,
        //availableTabs?.hasAlarmSetpoints,
        availableTabs?.hasHistory,
        //availableTabs?.hasProperties,
        availableTabs?.hasRunTickets,
        availableTabs?.hasValues,
        availableTabs?.hasAttachments,
        isPhone,
        objectType,
        properties,
        offlineState.offline,
        userState.customer?.customerType,
        activeTab,
        hasAlarmHistory,
        hasSettingsChanges,
        hasLatLong
    ]);

    const resetActionsV2 = useCallback(() => {

        if (objectViewState) {
            //const viewState = contextViewState?.viewState as IObjectViewState;
            const features = objectViewState.features;

            // need to move this into a utility somewhere
            const updateAndAddMenuItem = (results: ActionMenuItem[], action: BaseActions, disabled: boolean | undefined) => {
                const menuItem = objectActionItems.find(o => o.action === action);
                
                if (menuItem) {
                    results.push({...menuItem,
                        disabled: disabled,
                        order: menuItem.order
                    });
                }
            };

            // set available actions
            const currentActions: ActionMenuItem[] =  [];

            // //if (availableTabs?.hasValues) {

            //     //if ((features & ObjectFeaturesEnum.LiveData) > 0) {
            //         updateAndAddMenuItem(currentActions, BaseActionEnum.DemandScan, (features & ObjectFeaturesEnum.LiveData) === 0);
            //     //}

            //     //if ((features & ObjectFeaturesEnum.Writedown) > 0) {
            //         updateAndAddMenuItem(currentActions, BaseActionEnum.WriteDown, (features & ObjectFeaturesEnum.Writedown) === 0);
            //     //}
            // //}

            if (objectType === eLynxObjectTypeEnum.Device || objectType === eLynxObjectTypeEnum.Tank) {

                //if ((features & ObjectFeaturesEnum.LiveData) > 0) {
                    updateAndAddMenuItem(currentActions, BaseActionEnum.DemandScan, (features & ObjectFeaturesEnum.LiveData) === 0);
                //}

                //if ((features & ObjectFeaturesEnum.Writedown) > 0) {
                    updateAndAddMenuItem(currentActions, BaseActionEnum.WriteDown, (features & ObjectFeaturesEnum.Writedown) === 0);
                //}
            }


            if (!isPhone) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.Refresh, undefined);
            }


            if ((objectType === eLynxObjectTypeEnum.Device || objectType === eLynxObjectTypeEnum.Tank) &&
                    (activeTab === ObjectDetailsViewTabEnum.Values || 
                    (activeTab === ObjectDetailsViewTabEnum.History && 
                    (hasAlarmHistory || hasSettingsChanges)))) {

                updateAndAddMenuItem(currentActions, BaseActionEnum.Export, undefined);
            }

            //if (activeTab === ObjectDetailsViewTabEnum.RunTickets) {
            if (availableTabs?.hasRunTickets) {            
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddRunTicket, undefined);
            }

            // if (availableTabs?.hasProperties) {
            // }

            // if (state?.hasDefaults) {

            // }

            // if (availableTabs?.hasAlarmSetpoints) {

            // }

            // if (state?.hasAttachments) {

            // }

            //if (availableTabs?.hasHistory && properties?.canModifyMemos) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddMemo, !availableTabs?.hasHistory || !properties?.canModifyMemos);
            //}

            //if (/*!isPhone &&*/ !offlineState.offline && availableTabs?.hasAttachments && properties?.canModifyAttachments) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddAttachment, offlineState.offline || !availableTabs?.hasAttachments || !properties?.canModifyAttachments);
            //}

            if (!isPhone && !offlineState.offline && objectType !== eLynxObjectTypeEnum.TankBattery && userState.customer?.customerType === CustomerTypeEnum.Energy) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddDiagnositicEvent, undefined);
            }

            if (!offlineState.offline && objectType === eLynxObjectTypeEnum.Device && userState.customer?.customerType === CustomerTypeEnum.Water) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddEvent, undefined);
            }

            // if (state?.hasMembership) {

            // }

            // if (state?.hasRelationships) {

            // }

            // if (availableTabs?.hasRunTickets) {
            //     if ((objectViewState.features & ObjectFeaturesEnum.ManualEntry) > 0 || objectType !== eLynxObjectTypeEnum.Device) {
            //         if (!currentActions.some(o => o.action === BaseActionEnum.AddRunTicket)) {  
            //             updateAndAddMenuItem(currentActions, BaseActionEnum.AddRunTicket, undefined);
            //         }
            //     }
            // }
            // if (availableTabs?.hasRunTickets) {
            //     //if (!currentActions.some(o => o.action === BaseActionEnum.AddRunTicket)) {  
            //         updateAndAddMenuItem(currentActions, BaseActionEnum.AddRunTicket, 
            //                         //!availableTabs?.hasRunTickets || 
            //                         (objectViewState.features & ObjectFeaturesEnum.ManualEntry) === 0 || objectType === eLynxObjectTypeEnum.Device);
            //     //}
            // }


            //if (!offlineState.offline && properties?.canModify) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.AddAttribute, offlineState.offline || !properties?.canModify);
            //}


            let enabled = !offlineState.offline && ((objectViewState?.features2 ?? ObjectFeatures2Enum.None) & ObjectFeatures2Enum.ModifyPollingSchedule) > 0 ;
            //if (!offlineState.offline && ((objectViewState?.features2 ?? ObjectFeatures2Enum.None) & ObjectFeatures2Enum.ModifyPollingSchedule) > 0) {

                if (objectType === eLynxObjectTypeEnum.Device || objectType === eLynxObjectTypeEnum.Tank) {

                    const deviceProperties = properties as IObjectPropertiesDeviceModel;
                    enabled = enabled && deviceProperties && deviceProperties.tagPollingSchedules.length > 0;
                    //if (deviceProperties && deviceProperties.tagPollingSchedules.length > 0) {
                        updateAndAddMenuItem(currentActions, BaseActionEnum.ModifyPollingSchedule, !enabled);
                    //}
                }
                
            //}

            //setActions_old(currentActions);

            if (objectType === eLynxObjectTypeEnum.TankBattery || objectType === eLynxObjectTypeEnum.Tank) {
                const memoAction = currentActions.find(o => o.action === BaseActionEnum.AddMemo);
                const runTicketAction = currentActions.find(o => o.action === BaseActionEnum.AddRunTicket);

                if (memoAction && runTicketAction) {
                    const lowest = Math.min(memoAction.order ?? 0, runTicketAction.order ?? 0);
                    runTicketAction.order = lowest;
                    memoAction.order = lowest + 1;
                }
            }

            updateAndAddMenuItem(currentActions, BaseActionEnum.Favorite, undefined);


            //if (hasLatLong) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.MapIt, !hasLatLong);
            //}


            //if (!offlineState.offline && objectViewState.hybridServicesR1Enabled && objectViewState.platformObjectType === PlatformObjectTypeEnum.PollingServer) {
            if (!offlineState.offline && objectViewState.hybridServicesR1Enabled ) {
                updateAndAddMenuItem(currentActions, BaseActionEnum.ManageComm, objectViewState.platformObjectType !== PlatformObjectTypeEnum.PollingServer);
            }

            // TODO: Limited to devices for now...that's all the user story called for.
            if(!offlineState.offline && objectType === eLynxObjectTypeEnum.Device) {


                const hasAlarmPermissions = objectViewState?.accessPermissions?.some(o => o.functionalityType === AccessFunctionalityTypeEnum.Alarming && (o.access & AccessTypeEnum.Write_Change) > 0) ?? false;

                //if (hasAlarmPermissions)
                //{
                    updateAndAddMenuItem(currentActions, BaseActionEnum.DisableAllAlarms, !hasAlarmPermissions);
                    updateAndAddMenuItem(currentActions, BaseActionEnum.EnableAllAlarms, !hasAlarmPermissions);
                //}
            }
            

            setActions(currentActions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        }
    }, [
        objectViewState,
        setActions,
        //availableTabs?.hasAlarmSetpoints,
        availableTabs?.hasHistory,
        //availableTabs?.hasProperties,
        availableTabs?.hasRunTickets,
        //availableTabs?.hasValues,
        availableTabs?.hasAttachments,
        isPhone,
        objectType,
        properties,
        offlineState.offline,
        userState.customer?.customerType,
        activeTab,
        hasAlarmHistory,
        hasSettingsChanges,
        hasLatLong
    ]);

    // Set action menu items
    // NOTE: This effect is for items that apply to the entire object details view and do not change when the active tab changes.
    useEffect(() => {

        if (objectViewState) {
            if (headerAndActionsV2) {
                resetActionsV2();
            } else {
                resetActions();
            }
        }

    }, [
        headerAndActionsV2,
        objectViewState,
        resetActions,
        resetActionsV2
    ]);

    useEffect(() => {

            switch(objectType) {

                case eLynxObjectTypeEnum.Device:
                case eLynxObjectTypeEnum.Well:
                    if (activeTab === ObjectDetailsViewTabEnum.Properties || 
                            activeTab === ObjectDetailsViewTabEnum.Attachments || 
                            activeTab === ObjectDetailsViewTabEnum.Relationships ||
                            activeTab === ObjectDetailsViewTabEnum.ExternalContent) {
                        setViewOptionState(ViewOptionStateEnum.Hidden);
                    }

                    break;

                case eLynxObjectTypeEnum.TankBattery:
                case eLynxObjectTypeEnum.Tank:
                    if (activeTab === ObjectDetailsViewTabEnum.Properties || 
                        //activeTab === ObjectDetailsViewTabEnum.Values || 
                        activeTab === ObjectDetailsViewTabEnum.Attachments || 
                        activeTab === ObjectDetailsViewTabEnum.Relationships ||
                        activeTab === ObjectDetailsViewTabEnum.ExternalContent) {
                        setViewOptionState(ViewOptionStateEnum.Hidden);
                    }
                    break;
            }

        }, [activeTab,
            setViewOptionState,
            objectType
        ]);

    useEffect(() => {
        if (offlineState.offline && (activeTab === ObjectDetailsViewTabEnum.Properties || activeTab === ObjectDetailsViewTabEnum.AlarmSetpoints)) {
            setActiveTab(ObjectDetailsViewTabEnum.Values)
        }
    }, [
        offlineState.offline,
        activeTab,
        setActiveTab
    ]);


    return {
        deviceType: objectViewState?.deviceType,
        activeTab,
        setActiveTab,

        actions,
        setActions,
        resetActions,

        viewOptionState,
        setViewOptionState,

        hasSettingsChanges,
        hasAlarmHistory,
        setHasSettingsChanges,
        setHasAlarmHistory,

        latitude: properties?.latitude,
        longitude: properties?.longitude,

        configurationActions,
        setConfigurationsActions
    };
}

function getTabList(objectType: eLynxObjectTypeEnum) {

    return objectType === eLynxObjectTypeEnum.Device
        ? [{ key: ObjectDetailsViewTabEnum.Values, route: routesByViewID.get(UIViewEnum.Device_Details_Values) },
        { key: ObjectDetailsViewTabEnum.Properties, route: routesByViewID.get(UIViewEnum.Device_Details_Properties) },
        { key: ObjectDetailsViewTabEnum.Defaults, route: routesByViewID.get(UIViewEnum.Device_Details_Defaults) },
        { key: ObjectDetailsViewTabEnum.AlarmSetpoints, route: routesByViewID.get(UIViewEnum.Device_Details_AlarmSetpoints) },
        { key: ObjectDetailsViewTabEnum.Attachments, route: routesByViewID.get(UIViewEnum.Device_Details_Attachments) },
        { key: ObjectDetailsViewTabEnum.History, route: routesByViewID.get(UIViewEnum.Device_Details_History) },
        { key: ObjectDetailsViewTabEnum.RunTickets, route: routesByViewID.get(UIViewEnum.Device_Details_RunTickets) },
        { key: ObjectDetailsViewTabEnum.Relationships, route: routesByViewID.get(UIViewEnum.Device_Details_Relationships) },
        { key: ObjectDetailsViewTabEnum.ExternalContent, route: routesByViewID.get(UIViewEnum.Device_ExternalContent) }
        ]

        : objectType === eLynxObjectTypeEnum.Well
            ? [{ key: ObjectDetailsViewTabEnum.Values, route: routesByViewID.get(UIViewEnum.Well_Details_Values) },
            { key: ObjectDetailsViewTabEnum.Properties, route: routesByViewID.get(UIViewEnum.Well_Details_Properties) },
            { key: ObjectDetailsViewTabEnum.AlarmSetpoints, route: routesByViewID.get(UIViewEnum.Well_Details_AlarmSetpoints) },
            { key: ObjectDetailsViewTabEnum.Attachments, route: routesByViewID.get(UIViewEnum.Well_Details_Attachments) },
            { key: ObjectDetailsViewTabEnum.History, route: routesByViewID.get(UIViewEnum.Well_Details_History) },
            { key: ObjectDetailsViewTabEnum.Relationships, route: routesByViewID.get(UIViewEnum.Well_Details_Relationships) },
            { key: ObjectDetailsViewTabEnum.ExternalContent, route: routesByViewID.get(UIViewEnum.Well_ExternalContent) }                        
            /*{ key: ObjectDetailsViewTabEnum.RunTickets, route: routesByViewID.get(UIViewEnum.Well_RunTickets) }*/]
            


            : objectType === eLynxObjectTypeEnum.TankBattery
                ? [{ key: ObjectDetailsViewTabEnum.Values, route: routesByViewID.get(UIViewEnum.TankBattery_Details_Values) },
                { key: ObjectDetailsViewTabEnum.Properties, route: routesByViewID.get(UIViewEnum.TankBattery_Details_Properties) },
                { key: ObjectDetailsViewTabEnum.AlarmSetpoints, route: routesByViewID.get(UIViewEnum.TankBattery_Details_AlarmSetpoints) },
                { key: ObjectDetailsViewTabEnum.Attachments, route: routesByViewID.get(UIViewEnum.TankBattery_Details_Attachments) },
                { key: ObjectDetailsViewTabEnum.History, route: routesByViewID.get(UIViewEnum.TankBattery_Details_History) },
                { key: ObjectDetailsViewTabEnum.RunTickets, route: routesByViewID.get(UIViewEnum.TankBattery_Details_RunTickets) },
                { key: ObjectDetailsViewTabEnum.Relationships, route: routesByViewID.get(UIViewEnum.TankBattery_Details_Relationships) },
                { key: ObjectDetailsViewTabEnum.ExternalContent, route: routesByViewID.get(UIViewEnum.TankBattery_ExternalContent) }]


                : objectType === eLynxObjectTypeEnum.Tank
                    ? [{ key: ObjectDetailsViewTabEnum.Values, route: routesByViewID.get(UIViewEnum.Tank_Details_Values) },
                    { key: ObjectDetailsViewTabEnum.Properties, route: routesByViewID.get(UIViewEnum.Tank_Details_Properties) },
                    { key: ObjectDetailsViewTabEnum.AlarmSetpoints, route: routesByViewID.get(UIViewEnum.Tank_Details_AlarmSetpoints) },
                    { key: ObjectDetailsViewTabEnum.Attachments, route: routesByViewID.get(UIViewEnum.Tank_Details_Attachments) },
                    { key: ObjectDetailsViewTabEnum.History, route: routesByViewID.get(UIViewEnum.Tank_Details_History) },
                    { key: ObjectDetailsViewTabEnum.RunTickets, route: routesByViewID.get(UIViewEnum.Tank_Details_RunTickets) },
                    { key: ObjectDetailsViewTabEnum.Relationships, route: routesByViewID.get(UIViewEnum.Tank_Details_Relationships) },
                    { key: ObjectDetailsViewTabEnum.ExternalContent, route: routesByViewID.get(UIViewEnum.Tank_ExternalContent) }]
                    : [];
}
